// Mock auth service - works without backend using localStorage

export interface User {
  id: number;
  username: string;
  email: string;
  isMember: boolean;
}

interface StoredUser extends User {
  password: string; // In real app, this would be hashed
}

// Simple mock authentication - in production, use proper backend
export const mockLogin = (email: string, password: string): { user: User; token: string } | null => {
  const users = getStoredUsers();
  const user = users.find(u => u.email === email && u.password === password);
  
  if (!user) {
    return null;
  }
  
  const { password: _, ...userWithoutPassword } = user;
  const token = `mock_token_${user.id}_${Date.now()}`;
  
  return {
    user: userWithoutPassword,
    token,
  };
};

export const mockRegister = (username: string, email: string, password: string): User => {
  const users = getStoredUsers();
  
  // Check if user already exists
  if (users.some(u => u.email === email || u.username === username)) {
    throw new Error('User already exists');
  }
  
  const newUser: StoredUser = {
    id: Math.max(...users.map(u => u.id), 0) + 1,
    username,
    email,
    password, // In production, hash this
    isMember: false, // New users are not members by default
  };
  
  const updatedUsers = [...users, newUser];
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  
  const { password: _, ...userWithoutPassword } = newUser;
  return userWithoutPassword;
};

export const mockGetUser = (userId: number): User | null => {
  const users = getStoredUsers();
  const user = users.find(u => u.id === userId);
  if (!user) return null;
  
  const { password: _, ...userWithoutPassword } = user;
  return userWithoutPassword;
};

export const updateMembership = (userId: number, isMember: boolean): User | null => {
  const users = getStoredUsers();
  const index = users.findIndex(u => u.id === userId);
  if (index === -1) return null;
  
  users[index].isMember = isMember;
  localStorage.setItem('users', JSON.stringify(users));
  
  const { password: _, ...userWithoutPassword } = users[index];
  return userWithoutPassword;
};

const getStoredUsers = (): StoredUser[] => {
  const stored = localStorage.getItem('users');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }
  return [];
};

// Helper to get user from token (mock implementation)
export const getUserFromToken = (token: string): User | null => {
  // Extract user ID from mock token
  const match = token.match(/mock_token_(\d+)_/);
  if (!match) return null;
  
  const userId = parseInt(match[1], 10);
  return mockGetUser(userId);
};






